// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getQuoteState } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    quoteState: getQuoteState(state, state.pages.rfqConfirmation.quoteId),
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface RfqConfirmationDetailsStyles {
    detailsGridContainer?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    leftColumnInnerGridContainer?: GridContainerProps;
    jobNameGridItem?: GridItemProps;
    jobNameHeadingAndText?: SmallHeadingAndTextStyles;
    salesRepGridItem?: GridItemProps;
    salesRepHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    dateGridItem?: GridItemProps;
    dateLabelText?: TypographyPresentationProps;
    customerGridItem?: GridItemProps;
    customerHeadingAndText?: SmallHeadingAndTextStyles;
    shipToGridItem?: GridItemProps;
    shipToHeadingAndText?: SmallHeadingAndTextStyles;
    rightColumnGridItem?: GridItemProps;
    notesHeadingAndText?: SmallHeadingAndTextStyles;
    // XNG-Changes
    notesGridItem?: GridItemProps;
}

export const rfqConfirmationDetailsStyles: RfqConfirmationDetailsStyles = {
    detailsGridContainer: {
        gap: 10,
        css: css`
            margin-bottom: 20px;
        `,
    },
    leftColumnGridItem: { width: [12, 12, 12, 6, 6] },
    leftColumnInnerGridContainer: { gap: 10 },
    jobNameGridItem: { width: [6, 6, 4, 4, 4] },
    jobNameHeadingAndText: {
        heading: { transform: "initial", weight: "bold", size: 14 },
    },
    salesRepGridItem: { width: [6, 6, 4, 4, 4] },
    salesRepHeadingAndText: {
        heading: { transform: "initial", weight: "bold", size: 14 },
    },
    statusGridItem: { width: [6, 6, 4, 4, 4] },
    statusHeadingAndText: {
        heading: { transform: "initial", weight: "bold", size: 14 },
    },
    dateGridItem: {
        width: [6, 6, 4, 4, 4],
        css: css`
            flex-direction: column;
        `,
    },
    dateLabelText: { weight: "bold", size: 14 },
    customerGridItem: { width: 12 },
    customerHeadingAndText: {
        heading: { transform: "initial", weight: "bold", size: 14 },
    },
    shipToGridItem: { width: 12 },
    shipToHeadingAndText: {
        heading: { transform: "initial", weight: "bold", size: 14 },
    },
    rightColumnGridItem: { width: [12, 12, 12, 6, 6] },
    notesHeadingAndText: {
        heading: { transform: "initial", weight: "bold", size: 14 },
    },
};

const styles = rfqConfirmationDetailsStyles;

const RfqConfirmationDetails: FC<Props> = ({ quoteState }) => {
    if (!quoteState.value) {
        return null;
    }

    const quote = quoteState.value;

    // XNG-Change
    const shipToAddress = [
        quote.shipTo?.address1,
        quote.shipTo?.address2,
        quote.shipTo?.city,
        quote.shipTo?.state?.name,
        quote.shipTo?.postalCode,
        quote.shipTo?.phone,
    ];
    const shipToAddressToDisplay = shipToAddress.filter(Boolean).join(", ");

    // XNG-Changes: Structure
    return (
        <GridContainer {...styles.detailsGridContainer}>
            {quote.isJobQuote && (
                <GridItem {...styles.jobNameGridItem}>
                    <SmallHeadingAndText
                        heading={translate("Job Name")}
                        text={quote.jobName}
                        extendedStyles={styles.jobNameHeadingAndText}
                    />
                </GridItem>
            )}
            <GridItem {...styles.salesRepGridItem} data-test-selector="rfqConfirmation_salesRep">
                <SmallHeadingAndText
                    heading={translate("Sales Rep")}
                    text={quote.salespersonName}
                    extendedStyles={styles.salesRepHeadingAndText}
                />
            </GridItem>
            <GridItem {...styles.statusGridItem}>
                <SmallHeadingAndText
                    heading={translate("Status")}
                    text={quote.statusDisplay}
                    extendedStyles={styles.statusHeadingAndText}
                />
            </GridItem>
            {quote.orderDate && (
                <GridItem {...styles.dateGridItem}>
                    <Typography {...styles.dateLabelText}>{translate("Date Submitted")}</Typography>
                    <Typography as="p">
                        <LocalizedDateTime
                            dateTime={quote.orderDate}
                            options={{ year: "numeric", month: "numeric", day: "numeric" }}
                        />
                    </Typography>
                </GridItem>
            )}

            {/*  XNG-Change */}
            {/* {quote.billTo && (
                <GridItem {...styles.customerGridItem}>
                    <SmallHeadingAndText
                        heading={translate("Customer")}
                        text={quote.customerName}
                        extendedStyles={styles.customerHeadingAndText}
                    />
                </GridItem>
            )} */}

            {/*  XNG-Change */}
            {quote.billTo?.customerNumber && (
                <GridItem {...styles.customerGridItem}>
                    <SmallHeadingAndText
                        heading={translate("Account #")}
                        text={quote.billTo.customerNumber}
                        extendedStyles={styles.customerHeadingAndText}
                    />
                </GridItem>
            )}
            {/*  XNG-Change */}
            {quote.billTo?.companyName && (
                <GridItem {...styles.customerGridItem}>
                    <SmallHeadingAndText
                        heading={translate("Customer Name")}
                        text={quote.billTo.companyName}
                        extendedStyles={styles.customerHeadingAndText}
                    />
                </GridItem>
            )}

            {/*  XNG-Change */}
            {quote.shipTo?.customerSequence && (
                <GridItem {...styles.customerGridItem}>
                    <SmallHeadingAndText
                        heading={translate("Ship To Account #")}
                        text={quote.shipTo.customerSequence}
                        extendedStyles={styles.customerHeadingAndText}
                    />
                </GridItem>
            )}

            {/*  XNG-Change */}
            {quote.shipTo?.companyName && (
                <GridItem {...styles.customerGridItem}>
                    <SmallHeadingAndText
                        heading={translate("Ship To Customer Name")}
                        text={quote.shipTo.companyName}
                        extendedStyles={styles.customerHeadingAndText}
                    />
                </GridItem>
            )}

            <GridItem {...styles.shipToGridItem}>
                <SmallHeadingAndText
                    heading={translate(quote.fulfillmentMethod === "PickUp" ? "Pick Up" : "Ship To")}
                    // XNG-Change
                    text={shipToAddressToDisplay}
                    extendedStyles={styles.shipToHeadingAndText}
                />
            </GridItem>

            <GridItem {...styles.notesGridItem}>
                {quote.notes.trim().length > 0 && (
                    <SmallHeadingAndText
                        heading={translate("Notes")}
                        text={quote.notes}
                        extendedStyles={styles.notesHeadingAndText}
                    />
                )}
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(RfqConfirmationDetails),
    definition: {
        displayName: "Details",
        allowedContexts: ["RfqConfirmationPage"],
        fieldDefinitions: [],
        group: "RFQ Quote Confirmation",
    },
};

export default widgetModule;
